<template>
    <!-- Figma P1–P5 "Payment Method" (Under Working – S&R Flow): breadcrumb bar, Payment Method tabs card,
         method card, consent + signature card, Back / Cancel / Pay; Payment Summary on the right. -->
    <div v-if="!$helper.isEmpty(invoiceDetail)"
        class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pt-[35px] laptop:pt-[20px] pb-[20px] border-gray-300 font-roboto">
        <!-- below laptop: the round back button on the card's top edge (the same Back logic as before) -->
        <DetailBackButton :back="backClicked" />
        <div v-if="$route.query.is_from_mobile != 1"
            class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[15px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px]">
            <!-- list › detail › Payment Method, by the invoice's job type (S&R, Universal Quote, Replacement Quote) -->
            <template v-if="!isSilentLogin">
                <router-link :to="crumbSource.list" class="no-underline text-[#8479E1]">{{ crumbSource.listLabel }}</router-link>
                <span class="text-[#CCCCCC]">|</span>
            </template>
            <template v-if="invoiceDetail.quote_id">
                <router-link :to="crumbSource.detail + invoiceDetail.quote_id" class="no-underline text-[#8479E1]">{{ crumbSource.detailLabel }}</router-link>
                <span class="text-[#CCCCCC]">|</span>
            </template>
            <span class="text-[#000000]">Payment Method</span>
        </div>

        <div class="flex mobile:flex-col-reverse laptop:flex-row gap-[20px] desktop:gap-[30px] items-start">
            <!-- left column -->
            <div class="flex-1 min-w-0 w-full flex flex-col gap-[20px]">
                <!-- Payment Method card -->
                <div class="w-full shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
                    <div class="flex items-center gap-1">
                        <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
                        <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
                        <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">
                            {{ isApproved ? 'Approval' : 'Payment Method' }}
                        </p>
                    </div>
                    <p class="mt-[8px] mb-0 text-[13px] text-[#555555]">{{ methodSubtitle }}</p>
                    <!-- Tabs: wrap onto a second line on small screens (no sideways scroll) -->
                    <div v-if="!isApproved" ref="tabContainer"
                        class="flex flex-wrap gap-[8px] tablet:gap-[10px] mt-[15px] w-full">
                        <button v-for="(tab, index) in tabs" :key="index" :ref="el => tabRefs[tab.id] = el" type="button"
                            class="flex items-center gap-[8px] h-[34px] px-[16px] text-[13px] font-medium whitespace-nowrap shrink-0 focus:outline-none rounded-full border-1 transition-all"
                            :class="activeTab == tab.id
                                ? 'border-[#7695FF] text-white bg-[#7695FF]'
                                : 'border-[#E9ECF1] text-[#555555] bg-white laptop:hover:border-[#7695FF]'"
                            @click="tabClicked(tab.id)">
                            <span class="w-[6px] h-[6px] rounded-full" :class="activeTab == tab.id ? 'bg-white' : 'bg-[#CCCCCC]'" />
                            {{ tab.title }}
                        </button>
                    </div>
                </div>

                <!-- method card -->
                <div class="w-full shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
                    <div v-if="!isApproved">
                        <creditCardComponent v-if="activeTab === 0 && invoiceDetail.is_payable" ref="tab-0"
                            :stripe_id="invoiceDetail.stripe_id" @validation-change="isValid = $event" />
                        <!-- Cash -->
                        <cashComponent v-else-if="activeTab === 1" ref="tab-1" :invoice-detail="invoiceDetail" />
                        <!-- Check -->
                        <checkComponent v-else-if="activeTab === 2" ref="tab-2" @validation-change="isValid = $event" />
                        <!-- financing -->
                        <financingComponent v-else-if="activeTab === 3" ref="tab-3"
                            @validation-change="isValid = $event" :invoiceDetail="invoiceDetail"
                            :financialInstitutes="Array.isArray(financialInstitutes) ? financialInstitutes : []" :callback="financialCallback"
                            :linkGenerated="linkGenerated" :stripURL="stripURL" />
                        <!-- payment link -->
                        <div v-else-if="activeTab === 4 && !linkGenerated" ref="tab-4" class="flex flex-col items-center gap-[15px] py-[20px]">
                            <p class="m-0 text-[14px] text-[#333333] text-center">Click <span class="font-semibold">Generate Link</span> to
                                create your payment link. The link will appear here once generated.</p>
                            <button type="button" class="fxBtnPrimary" @click="generateLinkClicked()">Generate Link</button>
                        </div>
                        <div v-else-if="activeTab === 4 && linkGenerated" ref="tab-4" class="flex flex-col gap-[12px]">
                            <div class="flex items-center gap-1">
                                <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
                                <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
                                <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Financing</p>
                            </div>
                            <p class="m-0 text-[14px] text-[#333333]">Use this payment link to make an online payment. You can get financing through this option.</p>
                            <div class="relative w-full h-[40px] rounded-[8px] border-1 border-[#E2E5EC] bg-[#F7F8FA] overflow-hidden">
                                <input type="text" :value="stripURL" class="w-full h-full pl-[12px] pr-[48px] border-0 outline-none bg-transparent text-[13px] text-[#333333]" disabled>
                                <button type="button" class="absolute top-0 right-0 h-full w-[40px] flex items-center justify-center bg-[#00B7C2]" @click="copysharelink">
                                    <img src="../../assets/images/copy-icon.png" alt="Copy" class="w-4 h-4">
                                </button>
                            </div>
                            <p class="m-0 text-[12px] text-[#666666]">Click Continue to get financing.</p>
                        </div>
                    </div>
                    <!-- approved -->
                    <div v-else class="flex flex-col gap-[12px]">
                        <div class="flex items-center gap-1">
                            <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
                            <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
                            <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Approval</p>
                        </div>
                        <p class="m-0 text-[14px] text-[#333333]"><span class="font-semibold">${{
                            $helper.formatAmount(invoiceDetail?.amount ?? '0') }}</span> due today. Provide approval to proceed.</p>
                    </div>
                </div>

                <!-- consent + signature card -->
                <div v-if="activeTab != 4 && !isSignatureDisable"
                    class="w-full shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
                    <label class="flex items-start gap-[10px] m-0 cursor-pointer">
                        <input v-model="checkbox" type="checkbox" class="hidden peer">
                        <span class="w-[18px] h-[18px] mt-[1px] flex-shrink-0 flex items-center justify-center rounded-[4px] border-1 transition"
                            :class="checkbox ? 'bg-[#7695FF] border-[#7695FF]' : 'bg-white border-[#CCCCCC]'">
                            <svg v-if="checkbox" width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3 7.4l2.6 2.5L11 4.4" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                        </span>
                        <span v-if="isApproved" class="text-[13px] text-[#333333]">By signing, I confirm that I have reviewed and approved this quote.</span>
                        <span v-else class="text-[13px] text-[#333333]">By signing, I confirm that I have approved this quote and authorize the technician to complete checkout on my behalf.</span>
                    </label>

                    <div class="flex items-center justify-between w-full mt-[15px] mb-[8px]">
                        <label for="consumersignature" class="m-0 text-[13px] font-medium text-[#333333]">Consumer Signature<span class="ml-1 text-[#EE5851]">*</span></label>
                        <button type="button" :disabled="!isSignatureValid"
                            class="h-[26px] px-[14px] rounded-full border-1 text-[12px] transition-all border-[#F15A14] text-[#F15A14] bg-[#fff] hover:bg-[#FFF1EB] disabled:border-[#EFEFEF] disabled:bg-[#EFEFEF] disabled:text-[#AAAAAA]"
                            @click="clearSignature">Clear</button>
                    </div>
                    <div class="relative w-full h-[130px]">
                        <span v-if="!isSignatureValid" class="absolute inset-0 flex items-center justify-center text-[12px] text-[#AAAAAA] pointer-events-none">Sign here</span>
                        <VueSignaturePad ref="signaturePad" height="100%" width="100%" :options="{
                            onBegin: onBegin,
                            onEnd: onEnd
                        }" class="border-1 border-[#E2E5EC] outline-none w-full rounded-[10px] bg-white" />
                    </div>
                </div>

                <!-- actions -->
                <div class="flex flex-wrap items-center justify-between gap-[10px]">
                    <div class="flex items-center gap-[10px] ml-auto">
                        <button type="button"
                            class="h-[40px] px-[22px] rounded-full text-[14px] bg-[#EEF0F4] text-[#666666] hover:bg-[#E2E5EC]"
                            @click="backClicked">Cancel</button>
                        <button v-if="!isApproved" type="button" :disabled="isButtonDisable"
                            class="h-[40px] px-[22px] rounded-full text-[14px] font-medium bg-[#1FA971] text-white hover:bg-[#1A9563] disabled:bg-[#EEF0F4] disabled:text-[#AAAAAA]"
                            @click="payButtonClicked">
                            {{ isSignatureDisable ? 'Continue' : 'Pay $' + $helper.formatAmount(invoiceDetail?.amount ?? '0') }}
                        </button>
                        <!-- approved btn -->
                        <button v-else type="button" :disabled="isApprovedButtonDisable"
                            class="h-[40px] px-[22px] rounded-full text-[14px] font-medium bg-[#1FA971] text-white hover:bg-[#1A9563] disabled:bg-[#EEF0F4] disabled:text-[#AAAAAA]"
                            @click="approvedButtonClicked">Approve</button>
                    </div>
                </div>
            </div>

            <!-- Payment Summary -->
            <quoteDetailBox :invoice-detail="invoiceDetail" />
        </div>

        <PaymentFailedModal ref="paymentFailedModal" @another-method="chooseAnotherMethod" @try-again="checkCreditCard" />
    </div>
</template>

<script>
import {
    mapActions,
    mapGetters,
} from "vuex";

import cashComponent from '../../components/checkout/cashComponent.vue';
import checkComponent from '../../components/checkout/checkComponent.vue';
import creditCardComponent from '../../components/checkout/creditCardComponent.vue';
import financingComponent from '../../components/checkout/financingComponent.vue';
import quoteDetailBox from '../../components/checkout/quoteDetail.vue';
import DetailBackButton from '../../components/partialComponent/DetailBackButton.vue';
import PaymentFailedModal from '../../components/checkout/PaymentFailedModal.vue';
import { globals } from "../../main";
import { isSilentLogin } from "../../helpers/embedMode";
import { useToast } from "vue-toastification";
const toast = useToast();
export default {

    components: {
        creditCardComponent,
        cashComponent,
        checkComponent,
        financingComponent,
        quoteDetailBox,
        DetailBackButton,
        PaymentFailedModal
    },
    data() {
        return {
            checkbox: false,
            activeTab: 0,
            tabs: [
                { title: "Credit Card", id: 0 },
                { title: "Cash", id: 1 },
                { title: "Check", id: 2 }
            ],
            invoice_id: this.$route.params.id,
            isValid: false,
            isSignatureValid: false,
            signationImageFile: null,
            saveSignatureTimeout: null,
            tabRefs: {},
            linkGenerated: false,
            stripURL: '',
            ccPayload: {},
            isSignatureDisable: false
        }
    },
    computed: {
        ...mapGetters(["paymentLinkResponse", "creditCardIDResponse", "invoiceDetail", "quoteCheckoutResponse", "signaturePaymentLinkResponse", "financialInstitutes"]),

        // breadcrumb source from the invoice type ("Universal Quote", "Replacement Quote", else Service & Repair)
        crumbSource() {
            const type = String(this.invoiceDetail?.type || '').toLowerCase();
            if (type.includes('universal')) {
                return { list: '/work-order/universal-quotes', listLabel: 'Universal Quotes', detail: '/quote/detail/', detailLabel: 'Quote Details' };
            }
            if (type.includes('replacement')) {
                return { list: '/work-order/replacement-quote', listLabel: 'Replacement Quote History', detail: '/replacement-quote/detail/', detailLabel: 'Quote Details' };
            }
            return { list: '/work-order/service-and-repair', listLabel: 'Service & Repair', detail: '/job/detail/', detailLabel: 'Job Details' };
        },

        isSilentLogin() {
            return isSilentLogin();
        },

        // Figma P1–P6: the Payment Method subtitle follows the chosen tab
        methodSubtitle() {
            if (this.isApproved) return 'No balance is due. Please provide your approval to proceed.';
            if (this.activeTab == 1) return 'Cash selected. Collect the amount due from the consumer, then sign to complete.';
            if (this.activeTab == 2) return 'Check is selected. The panel below updates to match the chosen method.';
            if (this.activeTab == 3) return 'Choose the financial institution from the list.';
            return 'Please select your preferred payment option. Credit Card is selected by default.';
        },

        isApproved() {
            return !this.invoiceDetail.amount > 0
        },

        isButtonDisable() {
            if (this.isSignatureDisable) {
                if (this.stripURL.length <= 0) {
                    return true
                }
                return;
            }
            else {
                return !(this.checkbox && this.isValid && this.isSignatureValid)
            }
        },

        isApprovedButtonDisable() {
            return !(this.checkbox && this.isSignatureValid)
        }
    },

    watch: {
        financialInstitutes: function () {

        },

        paymentLinkResponse: function () {
            this.stripURL = this.paymentLinkResponse.session_url
            this.linkGenerated = this.stripURL.length > 0
            if (!this.linkGenerated) {
                toast.error("Problem while generating strip link.")
            }
        },

        creditCardIDResponse: function () {
            globals.$loader.show();
            this.ccCheckout(this.ccPayload)
        },

        invoiceDetail: function () {
            if (this.invoiceDetail.is_payable == 0) {
                toast.error("Job is not payable or has already been checked out.")
                this.$router.go(-1)
            } else {
                this.tabs = [
                    { title: "Credit Card", id: 0 },
                    { title: "Cash", id: 1 },
                    { title: "Check", id: 2 },
                    { title: "Financing", id: 3 }
                ]

                if (this.invoiceDetail) {
                    this.activeTab = this.invoiceDetail.preferred_payment_type == 'other' ? 3 : this.invoiceDetail.preferred_payment_type == 'check' ? 2 : this.invoiceDetail.preferred_payment_type == 'cash' ? 1 : 0
                }
            }
        },

        activeTab: function () {
            if (this.activeTab == 0 || this.activeTab == 2 || this.activeTab == 3) {
                this.isValid = false
            } else {
                this.isValid = true
            }

            // scroll logic
            this.$nextTick(() => {
                const el = this.tabRefs[this.activeTab]   // ✅ direct id se access
                if (el) {
                    el.scrollIntoView({
                        behavior: "smooth",
                        inline: "center",
                        block: "nearest"
                    })
                }
            })
        },

        signaturePaymentLinkResponse: function () {
            window.location.href = this.stripURL
        },

        quoteCheckoutResponse: function () {
            if (this.quoteCheckoutResponse.error_code == 'err_already_checked_out') {
                this.$router.go(-1)
                toast.error(this.quoteCheckoutResponse.message);
            } else if (this.quoteCheckoutResponse.status == true) {
                if (this.$route.query.is_from_mobile == '1') {

                    let redirectTo = localStorage.getItem("redirect_to") ?? ''
                    if (redirectTo == 'job_invoice') {
                        this.$router.go(-1)
                    }
                    else {
                        this.$router.replace({
                            name: 'checkoutSuccess',
                            query: {
                                is_from_mobile: Number(this.$route.query.is_from_mobile) === 1 ? 1 : 0
                            },
                        });
                    }
                } else {
                    toast.success("Your checkout was successful!")
                    this.$router.go(-1)
                }
            } else if (this.activeTab == 0 && this.isCardDecline(this.quoteCheckoutResponse)) {
                this.$refs.paymentFailedModal.open(this.declineReason(this.quoteCheckoutResponse))
            } else {
                toast.error(this.quoteCheckoutResponse.message);
            }
        },

    },

    mounted() {
        this.getInvoiceDetail({
            invoice_id: this.$route.params.id
        });

        this.getFinancialInstitutes({
            invoice_id: this.$route.params.id
        })
    },

    methods: {
        ...mapActions(["generatePaymentLink", "getCreditCardID", "getInvoiceDetail", "postCheckout", "uploadSignaturePaymentLink", "getFinancialInstitutes"]),

        generateLinkClicked() {
            this.generatePaymentLink({
                id: this.$route.params.id
            });
        },

        backClicked() {

            let redirectTo = localStorage.getItem("redirect_to") ?? ''
            if (this.$route.query.is_from_mobile == '1') {
                if (redirectTo == 'job_invoice') {
                    this.$router.go(-1)
                }
                else {
                    this.$router.replace({
                        name: 'checkoutFailed',
                        query: {
                            is_from_mobile: Number(this.$route.query.is_from_mobile) === 1 ? 1 : 0
                        },
                    });
                }

            } else {
                this.$router.go(-1)
            }
        },

        approvedButtonClicked() {
            this.$dialog.show({
                title: "Approve",
                text: "Are you sure you want to approve this quote?",
                onConfirm: () => {
                    this.cashCheckout()
                },
            });

        },

        checkValidation() {
            let currentRef = this.$refs[`tab-${this.activeTab}`]
            if (currentRef && currentRef.checkValidation) {
                return currentRef.checkValidation()
            } else {
                return true
            }
        },

        async checkCreditCard() {

            //new api implement here

            globals.$loader.show();

            let payload = await this.$refs[`tab-${this.activeTab}`].getData()
            this.ccPayload = payload
            if (payload.error) {
                globals.$loader.hide();
                if (this.isCardDecline(payload.error)) this.$refs.paymentFailedModal.open(this.declineReason(payload.error))
                else toast.error(payload.error.message)
                return
            }

            const failed = await this.getCreditCardID({
                invoice_id: this.$route.params.id,
                card_token: payload.token.id,
                silent_error: true
            });
            if (failed) {
                if (this.isCardDecline(failed)) this.$refs.paymentFailedModal.open(this.declineReason(failed))
                else toast.error(failed.message || 'Unable to process the card.')
            }
        },

        // Stripe decline (API passes Stripe's message/codes through), e.g. "Your card was declined.",
        // "Your card has insufficient funds.", decline_code "insufficient_funds"
        isCardDecline(res) {
            const body = res?.body && typeof res.body === 'object' ? res.body : {}
            const code = String(res?.code || res?.error_code || body.code || '')
            if (res?.decline_code || body.decline_code) return true
            if (['card_declined', 'expired_card', 'incorrect_cvc', 'incorrect_number', 'insufficient_funds'].includes(code)) return true
            return /declined|insufficient funds|card has expired|security code is incorrect/i.test(res?.message || '')
        },

        // red chip in the modal: "Decline code insufficient_funds · Your card has insufficient funds."
        declineReason(res) {
            const body = res?.body && typeof res.body === 'object' ? res.body : {}
            const code = res?.decline_code || body.decline_code
            const message = res?.message || ''
            return code ? `Decline code ${code}${message ? ' · ' + message : ''}` : message
        },

        chooseAnotherMethod() {
            this.$refs.tabContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },

        async payButtonClicked() {
            if (this.checkValidation()) {
                if (this.activeTab == 0) {
                    this.$dialog.show({
                        title: "Confirm Checkout",
                        text: "Are you sure you want to move forward with checkout?",
                        onConfirm: () => {
                            this.checkCreditCard()
                        },
                    });
                } else if (this.activeTab == 1) {
                    this.$dialog.show({
                        title: "Confirm Checkout",
                        text: "Are you sure you want to move forward with checkout?",
                        onConfirm: () => {
                            this.cashCheckout()
                        },
                    });
                } else if (this.activeTab == 2) {
                    let payload = await this.$refs[`tab-${this.activeTab}`].getData()
                    this.$dialog.show({
                        title: "Confirm Checkout",
                        text: "Are you sure you want to move forward with checkout?",
                        onConfirm: () => {
                            this.checkCheckout(payload)
                        },
                    });
                } else if (this.activeTab == 3) {
                    let payload = await this.$refs[`tab-${this.activeTab}`].getData()
                    if (payload?.type == 'PaymentLink') {
                        this.$dialog.show({
                            title: "Confirm Your Payment",
                            text: "By continuing, you will be redirected to our secure Stripe Checkout page to complete your payment.",
                            onConfirm: () => {
                                this.uploadSignaturePaymentLinkCall()
                            },
                        });
                    } else {
                        this.$dialog.show({
                            title: "Confirm Checkout",
                            text: "Are you sure you want to move forward with checkout?",
                            onConfirm: () => {
                                this.otherCheckout(payload)
                            },
                        });
                    }

                } else if (this.activeTab == 4) {
                    this.$dialog.show({
                        title: "Confirm Your Payment",
                        text: "By continuing, you will be redirected to our secure Stripe Checkout page to complete your payment.",
                        onConfirm: () => {
                            this.uploadSignaturePaymentLinkCall()
                        },
                    });
                }
            }

        },

        cashCheckout() {
            let signatureFile = this.getSignature()
            if (!signatureFile) return
            let formData = new FormData();
            formData.append("payment_type", 'cash');
            formData.append("invoice_id", this.invoice_id);
            formData.append("timestamp", new Date().getTime());
            formData.append("signature", signatureFile);
            formData.append("pac_id", this.invoiceDetail.pac_id);
            this.postCheckout(formData)
        },

        ccCheckout(payload) {
            let signatureFile = this.getSignature()
            if (!signatureFile) return
            let formData = new FormData();
            formData.append("payment_type", 'cc');
            formData.append("invoice_id", this.invoice_id);
            formData.append("card_token", payload.token.id);
            formData.append("timestamp", new Date().getTime());
            formData.append("signature", signatureFile);
            formData.append("card_id", this.creditCardIDResponse.card_id);
            formData.append("pac_id", this.invoiceDetail.pac_id);
            this.postCheckout(formData)
        },

        otherCheckout(payload) {
            let signatureFile = this.getSignature()
            if (!signatureFile) return
            let formData = new FormData();
            formData.append("payment_type", 'other');
            formData.append("invoice_id", this.invoice_id);
            formData.append("timestamp", new Date().getTime());
            formData.append("signature", signatureFile);
            formData.append("institute_id", payload.institute_id);
            formData.append("pac_id", this.invoiceDetail.pac_id);
            if (!this.$helper.isEmpty(payload.institute_name)) {
                formData.append("institute_name", payload.institute_name);
            }
            if (!this.$helper.isEmpty(payload.dealer_fee)) {
                formData.append("dealer_fee", payload.dealer_fee);
            }
            if (!this.$helper.isEmpty(payload.description)) {
                formData.append("description", payload.description);
            }
            if (!this.$helper.isEmpty(payload.plan_id)) {
                formData.append("plan_id", payload.plan_id);
            }
            this.postCheckout(formData)
        },

        checkCheckout(payload) {
            let signatureFile = this.getSignature()
            if (!signatureFile) return
            let formData = new FormData();
            formData.append("payment_type", 'check');
            formData.append("invoice_id", this.invoice_id);
            formData.append("front_image", payload.front_image);
            formData.append("back_image", payload.back_image);
            formData.append("timestamp", new Date().getTime());
            formData.append("signature", signatureFile);
            formData.append("pac_id", this.invoiceDetail.pac_id);
            this.postCheckout(formData)
        },

        uploadSignaturePaymentLinkCall() {

            window.location.href = this.stripURL

            // let signatureFile = this.getSignature()
            // if (!signatureFile) return
            // let formData = new FormData();
            // formData.append("invoice_id", this.invoice_id);
            // formData.append("timestamp", new Date().getTime());
            // formData.append("signature", signatureFile);
            // this.uploadSignaturePaymentLink(formData)
        },

        onBegin() {
            //this.isSignatureValid = false
            this.$refs.signaturePad.resizeCanvas();
            if (this.saveSignatureTimeout) {
                clearTimeout(this.saveSignatureTimeout);
            }
        },
        onEnd() {
            this.saveSignatureTimeout = setTimeout(() => {
                this.saveSignature();
            }, 1000);
            this.isSignatureValid = true
        },
        async saveSignature() {
            let result = { isEmpty: true, data: null }
            if (this.$refs.signaturePad) result = this.$refs.signaturePad.saveSignature();
            if (!result.isEmpty) {
                this.signationImageFile = await this.base64ToFile(result.data, "signature.png");
            }
        },

        base64ToFile(base64String, filename) {
            const arr = base64String.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], filename, { type: mime });
        },
        getSignature() {
            return this.signationImageFile
        },
        clearSignature() {
            this.$refs.signaturePad.clearSignature()
            this.isSignatureValid = false
            this.signationImageFile = null
            this.saveSignatureTimeout = null
        },
        copysharelink() {
            navigator.clipboard.writeText(this.stripURL)
            toast.success("URL copied successfully!");
        },

        financialCallback(option, data) {
            if (option == 'onTypeSelected') {
                this.isSignatureDisable = data.is_payment_link == 1 ? true : false
            } else if (option == 'onGenerateLinkClicked') {
                this.generateLinkClicked()
            }
        },

        tabClicked(id) {
            this.activeTab = id
            this.isSignatureDisable = false

        }
    }

}
</script>
<style scoped>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

/* Scrollbar width */
.customscrolbar::-webkit-scrollbar {
    width: 4px;
    /* horizontal ke liye height bhi set karna hoga */
    height: 4px;
}

/* Track */
.customscrolbar::-webkit-scrollbar-track {
    background: #fff;
    border-radius: 10px;
}

/* Thumb */
.customscrolbar::-webkit-scrollbar-thumb {
    background: #f1f1f1;
    border-radius: 10px;
}
</style>